import { MORTGAGE_TYPE_ENUM } from "../constants";
import {
  loan_amount_first_mortgage,
  loan_term,
  loan_to_value,
  combined_loan_to_value,
  final_total_loan_to_value,
  top_front_home_expense_ratio,
  actual_cash_down_payment,
  first_mortgage_payment_p_and_i,
  second_mortgage_payment_p_and_i,
  property_tax,
  offer_price_or_market_value
} from "../../calculations/loan";
import { debt_to_income_ratio } from "../../calculations/debt_info";
import { wrap_investment_properties } from "../../calculations/investment_reo";
import {
  hoa_dues_1,
  hoa_dues_2,
  mello_roos,
  home_owners_insurance_monthly,
  first_current_monthly_payment,
  amount_frequency_monthly
} from "../../calculations/property";
import Decimal from "decimal.js";
import { PURCHASE, REFINANCE } from "../../constants/document_types";
import { YES } from "../../constants/yes_no";
import {
  ARM_TERMS,
  ARM_3_1, ARM_5_1, ARM_7_1, ARM_10_1,
  ARM_3_6, ARM_5_6, ARM_7_6, ARM_10_6
} from "../../constants/loan_terms";

// ARM fixed-rate period in months, keyed by LANIS loan.term. Lets an ARM export
// as an adjustable loan with its fixed period (e.g. 5/6 ARM = 60 months fixed),
// so it round-trips as an ARM instead of a 30-year fixed.
const ARM_FIXED_MONTHS = {
  [ARM_3_1]: 36, [ARM_5_1]: 60, [ARM_7_1]: 84, [ARM_10_1]: 120,
  [ARM_3_6]: 36, [ARM_5_6]: 60, [ARM_7_6]: 84, [ARM_10_6]: 120
};

// Refinance purpose: cash-out vs rate-and-term. LANIS marks a rate/term refi
// with rate_and_term_refi = Yes; anything else is treated as cash-out.
export function skipRefinance({ options }) {
  return (options && options.documentType) !== REFINANCE;
}

export function populateRefinance(builder, { loan }) {
  const determination = loan.rate_and_term_refi === YES ? "NoCashOut" : "CashOut";
  builder.ele("RefinanceCashOutDeterminationType").txt(determination);
}

export function populateTermsOfLoan(builder, { document, loan, options }) {
  const documentType = options && options.documentType;
  const noteAmount = loan_amount_first_mortgage(
    documentType,
    loan,
    document.property,
    document.property_to_be_sold,
    document.is_selling_property
  );
  if (noteAmount != null && !noteAmount.isZero()) {
    builder.ele("BaseLoanAmount").txt(noteAmount.toString());
  }
  builder.ele("LienPriorityType").txt("FirstLien");
  // LoanPurposeType is Purchase/Refinance (matches POINT); the cash-out vs
  // rate-and-term distinction rides in the separate REFINANCE element below.
  const loanPurposeType = documentType === PURCHASE ? "Purchase" : "Refinance";
  builder.ele("LoanPurposeType").txt(loanPurposeType);
  const ltv = loan_to_value(
    documentType,
    loan,
    document.property,
    document.property_to_be_sold,
    document.is_selling_property
  );
  if (!ltv.isZero()) {
    builder.ele("LoanToValueRatioPercent").txt(ltv.toDecimalPlaces(3).toString());
  }
  const mortgageType = MORTGAGE_TYPE_ENUM[loan.type];
  if (mortgageType) {
    builder.ele("MortgageType").txt(mortgageType);
  }
  if (noteAmount != null && !noteAmount.isZero()) {
    builder.ele("NoteAmount").txt(noteAmount.toString());
  }
  if (loan.rate && loan.rate !== "0") {
    builder.ele("NoteRatePercent").txt(loan.rate);
  }
  if (documentType === PURCHASE) {
    const purchasePrice = offer_price_or_market_value(documentType, loan, document.property);
    if (!purchasePrice.isZero()) {
      builder.ele("PurchasePriceAmount").txt(purchasePrice.toString());
    }
  }
}

export function populateLoanDetail(builder, { document, loan }) {
  if (document.applicant.first_contact_date) {
    builder.ele("ApplicationReceivedDate").txt(document.applicant.first_contact_date);
  }
  // Balloon only when explicitly YES (e.g. a 30/15 - amortized 30, due in 15).
  // balloon_payment is a YES/NO/TBD field, so a plain truthiness test wrongly
  // flagged "NO"/"TBD" as a balloon. A standard 30/20/15-yr fixed is not a balloon.
  const hasBalloon = loan.balloon_payment === YES;
  builder.ele("BalloonIndicator").txt(String(hasBalloon));
  builder.ele("BorrowerCount").txt(String(document.applicant.borrowers.length));
  builder.ele("BuydownTemporarySubsidyFundingIndicator").txt("false");
  builder.ele("ConstructionLoanIndicator").txt("false");
  builder.ele("ConversionOfContractForDeedIndicator").txt("false");
  builder.ele("EnergyRelatedImprovementsIndicator").txt("false");
  const armFixedMonths = ARM_FIXED_MONTHS[loan.term];
  if (armFixedMonths) {
    builder.ele("InitialFixedPeriodEffectiveMonthsCount").txt(String(armFixedMonths));
  }
  const hasInterestOnly = loan.interest_only_period &&
    loan.interest_only_period !== "0" &&
    loan.interest_only_period !== "";
  builder.ele("InterestOnlyIndicator").txt(String(!!hasInterestOnly));
  builder.ele("NegativeAmortizationIndicator").txt("false");
  builder.ele("PrepaymentPenaltyIndicator").txt(
    String(loan.prepayment_penalty === YES || loan.prepayment_penalty === "Y")
  );
  builder.ele("RenovationLoanIndicator").txt("false");
}

// MISMO CombinedLTVRatioPercent is the TOTAL of all liens over value (industry
// CLTV / TLTV) - the App's "Final Total Loan to Value (TLTV)", NOT LANIS's
// internal combined_loan_to_value (which is only the subordinate portion, shown
// as the "CLTV" field). Exporting the subordinate-only figure produced a CLTV
// smaller than the LTV, which is impossible.
export function skipCombinedLTVS({ document, loan, options }) {
  if (!loan) return true;
  const documentType = options && options.documentType;
  const tltv = final_total_loan_to_value(
    documentType,
    loan,
    document.property,
    document.property_to_be_sold,
    document.is_selling_property
  );
  return tltv.isZero();
}

export function populateCombinedLTV(builder, { document, loan, options }) {
  const documentType = options && options.documentType;
  const tltv = final_total_loan_to_value(
    documentType,
    loan,
    document.property,
    document.property_to_be_sold,
    document.is_selling_property
  );
  if (!tltv.isZero()) {
    builder.ele("CombinedLTVRatioPercent").txt(tltv.toDecimalPlaces(3).toString());
  }
}

export function populateQualifyingRatios(builder, { document, loan, options }) {
  const documentType = options && options.documentType;
  const borrowers = document.applicant.borrowers;
  const investmentProperties = wrap_investment_properties(
    document.property,
    document.investment_properties,
    false
  );

  const topRatio = top_front_home_expense_ratio(
    documentType,
    loan,
    document.property,
    borrowers,
    investmentProperties,
    document.property_to_be_sold,
    document.is_selling_property
  );
  if (!topRatio.isZero()) {
    builder.ele("TopRatioPercent").txt(topRatio.toDecimalPlaces(3).toString());
  }

  const bottomRatio = debt_to_income_ratio(
    documentType,
    borrowers,
    investmentProperties,
    document.accountSummaries,
    document.property,
    document.residences,
    loan,
    document.property_to_be_sold,
    document.is_selling_property
  );
  if (!bottomRatio.isZero()) {
    builder.ele("BottomRatioPercent").txt(bottomRatio.toDecimalPlaces(3).toString());
  }
}

export function populateAmortization(builder, { loan }) {
  if (!loan.term) return;
  const isArm = ARM_TERMS.includes(loan.term);
  builder.ele("AmortizationType").txt(isArm ? "AdjustableRate" : "Fixed");
  const periodCount = loan_term(loan).toString();
  builder.ele("LoanAmortizationPeriodCount").txt(periodCount);
  builder.ele("LoanAmortizationPeriodType").txt("Month");
}

// The INTEREST_RATE_ADJUSTMENT / lifetime rate ceiling only applies to an ARM.
// A fixed-rate loan (30/20/15-yr) keeps its rate for the full term, so it must
// not carry a rate-adjustment block.
export function skipAdjustment({ loan }) {
  return (
    !loan ||
    !loan.rate ||
    loan.rate === "0" ||
    !ARM_TERMS.includes(loan.term)
  );
}

export function populateAdjustment(builder, { loan }) {
  const ir = builder.ele("INTEREST_RATE_ADJUSTMENT");
  const rule = ir.ele("INTEREST_RATE_LIFETIME_ADJUSTMENT_RULE");
  rule.ele("CeilingRatePercent").txt(loan.rate);
}

export function skipClosingInformation({ document, loan, options }) {
  const documentType = options && options.documentType;
  if (documentType !== PURCHASE) return true;
  const downPaymentAmount = actual_cash_down_payment(documentType, loan, document.property);
  return !downPaymentAmount || downPaymentAmount.isZero();
}

export function populateClosingInformation(builder, { document, loan, options }) {
  const documentType = options && options.documentType;
  const downPaymentAmount = actual_cash_down_payment(documentType, loan, document.property);
  builder.ele("CashFromBorrowerAtClosingAmount").txt(downPaymentAmount.toDecimalPlaces(2).toString());
}

export function populateHMDALoan(builder) {
  builder.ele("HMDA_HOEPALoanStatusIndicator").txt("false");
}

export function populateMaturity(builder, { loan }) {
  if (loan && loan.term) {
    const maturityCount = (loan.balloon_payment === YES && loan.balloon_payment_period)
      ? loan.balloon_payment_period
      : loan_term(loan).toString();
    builder.ele("LoanMaturityPeriodCount").txt(maturityCount);
  }
  builder.ele("LoanMaturityPeriodType").txt("Month");
}

export function populateOriginationSystems(builder) {
  builder.ele("LoanOriginationSystemName").txt("LANIS Tier One");
}

// No down payment on a refinance (and none when the amount is zero) - skip the
// whole DOWN_PAYMENTS block so we don't emit an empty <DOWN_PAYMENT/> element.
export function skipDownPayments({ document, loan, options }) {
  const documentType = options && options.documentType;
  if (documentType !== PURCHASE || !loan) return true;
  const amount = actual_cash_down_payment(documentType, loan, document.property);
  return !amount || amount.isZero();
}

export function populateDownPayment(builder, { document, loan, options }) {
  const documentType = options && options.documentType;
  if (documentType !== PURCHASE) return;
  const downPaymentAmount = actual_cash_down_payment(documentType, loan, document.property);
  if (downPaymentAmount && !downPaymentAmount.isZero()) {
    builder.ele("DownPaymentAmount").txt(downPaymentAmount.toString());
  }
}

export function populateHousingExpenses(builder, { document, loan, options }) {
  const documentType = options && options.documentType;
  const property = document.property;

  function addExpense(type, amount, timing = "Proposed") {
    if (amount && !amount.isZero()) {
      const exp = builder.ele("HOUSING_EXPENSE");
      exp.ele("HousingExpensePaymentAmount").txt(amount.toDecimalPlaces(2).toString());
      exp.ele("HousingExpenseTimingType").txt(timing);
      exp.ele("HousingExpenseType").txt(type);
    }
  }

  const pi = first_mortgage_payment_p_and_i(
    documentType,
    loan,
    property,
    document.property_to_be_sold,
    document.is_selling_property
  );
  addExpense("FirstMortgagePrincipalAndInterest", pi);

  // A refinance that carries a NEW 2nd - a pure 2nd-lien product
  // (HELOC / Fixed Rate Second) OR a new 1st that also adds a 2nd
  // ("Equity Remaining w/ 2nd Loan") - exports the new 2nd's P&I as
  // OtherMortgageLoanPrincipalAndInterest (matches POINT's HELOC export). The
  // existing/new 1st is exported above as FirstMortgagePrincipalAndInterest.
  const secondPi = second_mortgage_payment_p_and_i(
    documentType,
    loan,
    property,
    document.property_to_be_sold,
    document.is_selling_property
  );
  if (documentType === REFINANCE && secondPi.gt(0)) {
    addExpense("OtherMortgageLoanPrincipalAndInterest", secondPi);
  }

  const insurance = home_owners_insurance_monthly(documentType, property);
  addExpense("HomeownersInsurance", insurance);

  const tax = property_tax(documentType, loan, property);
  addExpense("RealEstateTax", tax);

  const hoa = hoa_dues_1(property, loan).add(hoa_dues_2(property, loan));
  addExpense("HomeownersAssociationDuesAndCondominiumFees", hoa);

  const melloroos = mello_roos(property, loan);
  addExpense("OtherHousingExpense", melloroos);

  // Present (current) housing expenses. On a Purchase this is the departing home
  // being sold; on a Refinance it is the subject property's CURRENT loan. Both
  // read the current 1st/2nd payment from the Current Property Info / Selling
  // Property page (document.property_to_be_sold); the escrow differs by app.
  // Populates POINT's "Present Housing" column (refi needs the total for the
  // Net Tangible Benefit test).
  // Export present (current) housing whenever there's a departing/subject property
  // to report: a Purchase that is selling the current home, OR any Refinance (the
  // subject property's current loan lives on property_to_be_sold regardless of
  // whether it is being sold). A retain-refinance has is_selling_property = NO, so
  // gating only on that dropped present housing entirely — POINT's Present Housing
  // column came in blank.
  if (document.is_selling_property === YES || documentType === REFINANCE) {
    const pts = document.property_to_be_sold || {};

    addExpense(
      "FirstMortgagePrincipalAndInterest",
      first_current_monthly_payment(pts),
      "Current"
    );
    addExpense(
      "OtherMortgageLoanPrincipalAndInterest",
      new Decimal(pts.second_current_monthly_payment || 0).add(
        pts.miscellaneous_housing_payment || 0
      ),
      "Current"
    );

    if (documentType === PURCHASE) {
      // Departing home escrow, entered (monthly/frequency) on the Selling
      // Property page.
      addExpense(
        "HomeownersInsurance",
        amount_frequency_monthly(
          pts.present_home_insurance,
          pts.present_home_insurance_frequency
        ),
        "Current"
      );
      addExpense(
        "RealEstateTax",
        amount_frequency_monthly(
          pts.present_property_tax,
          pts.present_property_tax_frequency
        ),
        "Current"
      );
      addExpense(
        "HomeownersAssociationDuesAndCondominiumFees",
        amount_frequency_monthly(
          pts.present_hoa1,
          pts.present_hoa1_frequency
        ).add(
          amount_frequency_monthly(pts.present_hoa2, pts.present_hoa2_frequency)
        ),
        "Current"
      );
      addExpense(
        "OtherHousingExpense",
        amount_frequency_monthly(
          pts.present_mello_roos,
          pts.present_mello_roos_frequency
        ),
        "Current"
      );
      addExpense(
        "MIPremium",
        new Decimal(pts.existing_mortgage_insurance || 0),
        "Current"
      );
    } else {
      // Refinance: present and proposed are the same property, so the current
      // escrow equals the subject's taxes / insurance / HOA / other computed
      // above for the Proposed rows.
      addExpense("HomeownersInsurance", insurance, "Current");
      addExpense("RealEstateTax", tax, "Current");
      addExpense("HomeownersAssociationDuesAndCondominiumFees", hoa, "Current");
      addExpense("OtherHousingExpense", melloroos, "Current");
    }
  }
}
